import React, { useState, useEffect, useCallback } from 'react';
import {
  Database, Loader2, RefreshCw, Trash2, CheckCircle2, AlertCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin card: introspects + busts the SSR cache that powers the bot-friendly
 * `/api/seo/render/*` endpoints used by LLM crawlers (Perplexity, ChatGPT,
 * Claude, etc.). Every key here is a pre-rendered HTML page that AI answer
 * engines can crawl; flushing forces the next bot hit to re-render from the
 * latest DB state — useful after manual edits or bulk operations.
 */
const SsrCacheCard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const token = () => localStorage.getItem('session_token');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/seo/cache/stats`, {
        headers: { Authorization: `Bearer ${token()}` },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      setStats(await res.json());
    } catch (e) {
      setError(e.message || 'Failed to load SSR cache stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const flashNotice = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(null), 2500);
  };

  const flushAll = async () => {
    if (!window.confirm('Flush the entire SSR cache? The next bot hit on every page will re-render from fresh DB data.')) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/seo/cache/flush`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      const data = await res.json();
      flashNotice(`Flushed ${data.removed} cached page${data.removed === 1 ? '' : 's'}.`);
      await fetchStats();
    } catch (e) {
      setError(e.message || 'Flush failed');
    } finally {
      setBusy(false);
    }
  };

  const invalidateOne = async (key) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/seo/cache/invalidate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token()}`,
        },
        credentials: 'include',
        body: JSON.stringify([key]),
      });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      const data = await res.json();
      flashNotice(`Invalidated "${key}" (${data.removed}).`);
      await fetchStats();
    } catch (e) {
      setError(e.message || 'Invalidate failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="ssr-cache-card"
      className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-5 mt-6"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Database className="h-5 w-5 text-[#22C55E]" />
          <h3 className="text-lg font-bold text-white">SSR Cache (LLM crawlers)</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={fetchStats}
            disabled={loading || busy}
            data-testid="ssr-cache-refresh"
            aria-label="Refresh"
            className="p-1.5 rounded-full bg-[#1e2a45] text-gray-400 hover:text-white hover:bg-[#2d3a56] transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={flushAll}
            disabled={busy || loading || (stats && stats.total === 0)}
            data-testid="ssr-cache-flush"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/30 text-xs font-semibold transition-colors disabled:opacity-50 disabled:hover:bg-red-500/20"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Flush all
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-500 mb-4">
        Pre-rendered pages served to Perplexity, ChatGPT, Claude, and other JS-less crawlers.
        Invalidation happens automatically on market resolve, idea approval, and market creation
        — use <b>Flush all</b> after manual DB edits or mass imports.
      </p>

      {error && (
        <div className="flex items-center gap-2 text-red-400 text-sm mb-3" data-testid="ssr-cache-error">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {notice && (
        <div className="flex items-center gap-2 text-[#22C55E] text-sm mb-3" data-testid="ssr-cache-notice">
          <CheckCircle2 className="h-4 w-4" />
          {notice}
        </div>
      )}

      {loading && !stats ? (
        <div className="flex items-center justify-center py-6 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading cache stats…
        </div>
      ) : stats ? (
        <>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-[#050810] rounded-xl p-3 border border-[#1e2a45]">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
                Total entries
              </div>
              <div
                className="text-2xl font-bold text-white mt-1"
                data-testid="ssr-cache-total"
              >
                {stats.total}
              </div>
            </div>
            <div className="bg-[#050810] rounded-xl p-3 border border-[#1e2a45]">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
                Live (not expired)
              </div>
              <div
                className="text-2xl font-bold text-[#22C55E] mt-1"
                data-testid="ssr-cache-live"
              >
                {stats.live}
              </div>
            </div>
          </div>

          {stats.keys && stats.keys.length > 0 ? (
            <div className="rounded-xl border border-[#1e2a45] overflow-hidden">
              <div className="px-3 py-2 bg-[#0b1222] text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
                Cached keys ({stats.keys.length})
              </div>
              <ul className="max-h-64 overflow-y-auto divide-y divide-[#1e2a45]">
                {stats.keys.map((k) => (
                  <li
                    key={k}
                    data-testid={`ssr-cache-key-${k}`}
                    className="flex items-center justify-between px-3 py-2 text-sm hover:bg-[#0b1222]"
                  >
                    <code className="text-gray-200 text-xs">{k}</code>
                    <button
                      type="button"
                      onClick={() => invalidateOne(k)}
                      disabled={busy}
                      className="text-[11px] font-semibold text-red-400 hover:text-red-300 disabled:opacity-50"
                      data-testid={`ssr-cache-invalidate-${k}`}
                    >
                      Invalidate
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-xs text-gray-500 italic">
              Cache is empty. Pages will be rendered on the next bot hit.
            </p>
          )}
        </>
      ) : null}
    </div>
  );
};

export default SsrCacheCard;
